import React, { useEffect, useState, useCallback, useRef } from "react";

import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  MarkerClusterer,
  OverlayView,
} from "@react-google-maps/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "@fortawesome/fontawesome-svg-core/styles.css";
import {
  faRectangleList,
  faLocationPin,
} from "@fortawesome/free-solid-svg-icons";
import Draggable from "react-draggable";
import { featureService } from "services/FeatureService";
import { useTranslation } from "react-i18next";
import "./GoogleMap.scss";
import { filtersAtomObject, originalDataAtomObject, expandedJobDetailsAtomObject, showMapAtomBool } from "../../state";
import { useAtom } from "jotai";
import { Link } from "react-router-dom";
import useJobDetails from "../../hooks/useJobDetails";
const containerStyle = {
  width: "100%",
  height: "100%",
};

const Map = ({ toggleMap, nextPageSearch }) => {
  const [originalData] = useAtom(originalDataAtomObject);
  const [filters] = useAtom(filtersAtomObject);
  const { totalCount, jobs, nextPageToken } = originalData;
  const loadCount = jobs.length;
  const userLocation = filters.commute;

  const [windowHeight, setWindowHeight] = useState(0);
  const [dragAmount, setDragAmount] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [minHeight, setMinHeight] = useState(0);
  const [maxHeight, setMaxHeight] = useState(0);
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const [, setInitialHeight] = useState(0);
  const isMobile = window.innerWidth <= 768;
  const [clickedCluster, setClickedCluster] = useState(null);
  const draggableRef = useRef(null);
  const [isNextButtonVisible, setIsNextButtonVisible] = useState(true);

  const [expandedJobDetails] = useAtom(expandedJobDetailsAtomObject);

  const jobDetails = useJobDetails();
  const [showMap, setShowMap] = useAtom(showMapAtomBool);
  const resetDrawerState = () => {
    const screenHeight = window.innerHeight;
    const initialHeight = Math.max(
      screenHeight * 0.5,
      0.5 * window.innerHeight
    );
    setMinHeight(initialHeight);
    setMaxHeight(initialHeight);
    const initialY = screenHeight - initialHeight;
    setDragAmount(initialHeight);
    setPosition({ x: 0, y: initialY }); // Set position to the initial position
    setInitialPosition({ x: 0, y: initialY });
    setInitialHeight(initialHeight);
    setWindowHeight(screenHeight);
  };
  const { t } = useTranslation();

  useEffect(() => {
    resetDrawerState();
  }, []); // Run once on mount

  useEffect(() => {
    const handleResize = () => {
      const screenHeight = window.innerHeight;
      setWindowHeight(screenHeight);

      const updatedMinHeight = Math.min(minHeight, screenHeight);
      const updatedMaxHeight = Math.min(maxHeight, screenHeight);
      setMinHeight(updatedMinHeight);
      setMaxHeight(updatedMaxHeight);

      resetDrawerState(); // Call the function to reset height and position
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [minHeight, maxHeight]);

  const handleDrag = (e, ui) => {
    const { y } = ui;
    const newHeight = windowHeight - y;
    setDragAmount(newHeight);
  };

  const [filteredJobCount, setFilteredJobCount] = useState(jobs.length);
  const [mapBounds, setMapBounds] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [showList, setShowList] = useState(false);
  const [, setMapZoom] = useState(10);
  const [center, setCenter] = useState();
  const [zoom, setZoom] = useState(10); // Add zoom state
  const [selectedJobIndex, setSelectedJobIndex] = useState(-1);
  const jobDataItemsRef = useRef([]);
  const [libraries] = useState(["places"]);
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: featureService.mapKey(),
    libraries,
  });

  const markerColor = window.markerColour || "000000";
  const activeMarkerColor = window.activeMarkerColor || "000000";
  const customMapStyle = window.customMapStyle || [];

  useEffect(() => {
    if (
      userLocation &&
      userLocation.startingLocation?.latlng?.lat !== undefined &&
      userLocation.startingLocation?.latlng?.lng !== undefined
    ) {
      setCenter({
        lat: parseFloat(userLocation.startingLocation?.latlng?.lat),
        lng: parseFloat(userLocation.startingLocation?.latlng?.lng),
      });
    }
  }, [userLocation]);

  const [map, setMap] = useState(null);

  const onLoad = useCallback((map) => {
    // setZoom(map.getZoom());

    setMap(map);
    setMapZoom(map.getZoom());
  });

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleNextPage = () => {
    // Call the nextPageSearch function with the nextPageToken
    nextPageToken && nextPageSearch(nextPageToken);
  };

  const filterJobListings = () => {
    if (mapBounds) {
      const filteredListings = jobs.filter((job) => {
        if (job.location == null) return null;
        const jobLatLng = new window.google.maps.LatLng(
          job.location.lat,
          job.location.lng
        );
        return mapBounds.contains(jobLatLng);
      });
      return filteredListings;
    }
    return jobs;
  };

  const updateFilteredJobCount = () => {
    if (mapBounds) {
      const filteredListings = jobs.filter((job) => {
        if (job.location == null) return null;
        const jobLatLng = new window.google.maps.LatLng(
          job.location.lat,
          job.location.lng
        );
        return mapBounds.contains(jobLatLng);
      });
      setFilteredJobCount(filteredListings.length);
    }
  };

  const handleZoomChanged = () => {
    if (map) {
      const bounds = map.getBounds();
      if (bounds) {
        setMapBounds(bounds);
        updateFilteredJobCount();

        jobs.every((job) => {
          if (job.location == null) return true;
          const jobLatLng = new window.google.maps.LatLng(
            job.location.lat,
            job.location.lng
          );
          return bounds.contains(jobLatLng);
        });

        setIsNextButtonVisible(true);
      }
    }
  };

  useEffect(() => {
    if (map) {
      setShowList(true);

      setTimeout(() => {
        const bounds = new window.google.maps.LatLngBounds();

        jobs.forEach((marker) => {
          marker.location &&
            bounds.extend({
              lat: marker.location.lat,
              lng: marker.location.lng,
            });
        });

        center &&
          bounds.extend({
            lat: center.lat,
            lng: center.lng,
          });

        // Center and zoom out to include all markers and the center point
        map.fitBounds(bounds);

        // Update the zoom state when the zoom changes
        window.google.maps.event.addListener(map, "zoom_changed", () => {
          setZoom(map.getZoom());
        });

        // Set the initial zoom
        // setZoom(map.getZoom());
        map.addListener("bounds_changed", handleZoomChanged);
        map.addListener("zoom_changed", handleZoomChanged);
      }, 800);
    }
  }, [map, jobs, center, nextPageSearch]);

  const handleMarkerClick = (index) => {
    setActiveMarker((prevState) => (prevState === index ? -1 : index));

    if (showList === false) {
      // Deactivate the marker
      setShowList(true);
    }

    // setSelectedJobIndex(index);
    setSelectedJobIndex((prevState) => (prevState === index ? -1 : index));
    const displayCountDiv = document.querySelector(".display-count");
    const jobResultsContainer = document.querySelector(".job-listing");

    if (
      jobDataItemsRef.current[index] &&
      displayCountDiv &&
      jobResultsContainer
    ) {
      const item = jobDataItemsRef.current[index];
      const itemTop = item.offsetTop;
      const itemHeight = item.clientHeight;
      const scrollPosition =
        itemTop - displayCountDiv.clientHeight + itemHeight;
      setTimeout(() => {
        jobDataItemsRef.current[index].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        jobResultsContainer.scrollTop = scrollPosition;
      }, 100);
    }
  };

  const handleJobDataItemClick = (index) => {
    setActiveMarker((prevState) => (prevState === index ? -1 : index));
    setSelectedJobIndex((prevState) => (prevState === index ? -1 : index));
    if (showList === false) {
      // Deactivate the marker
      setShowList(true);
    }
  };

  useEffect(() => {
    updateFilteredJobCount();
  }, [zoom, loadCount]);

  // Radius Calc

  // const travelTimeMinutes = userLocation?.distance ?? 0;
  // const speedByMode = {
  //   3: 5, // 5 miles/hour
  //   4: 15, // 15 miles/hour
  //   1: 30, // 30 miles/hour
  //   5: 10, // 10 miles/hour (public transport)
  // };
  // const selectedMode = userLocation?.method.id ?? 0;
  // const averageSpeedMilesPerHour = speedByMode[selectedMode];
  // const radiusMeters = (averageSpeedMilesPerHour * travelTimeMinutes) / 60;

  const [, setClusterColor] = useState(markerColor);

  useEffect(
    (clusteIndex) => {
      setClusterColor(activeMarker !== null ? activeMarkerColor : markerColor);
    },
    [activeMarker]
  );

  const handleClusterClick = (clusterIndex) => {
    setClickedCluster(clusterIndex.markerClusterer.markers?.length);
  };

  const CustomInfoWindow = ({ activeMarker, job }) => {
    if (job.commuteTime === 0) {
      return null; // If commuteTime is 0, don't render the info window
    }
    return (
      <div
        style={{
          position: "absolute",
          transform: "translate(-50%, -100%)",
          background: `#${activeMarker === null
              ? markerColor
              : activeMarker === job.id
                ? activeMarkerColor
                : markerColor
            }`,
          padding: "5px",
          color: "white",
          borderRadius: "5px",
          width: "65px",
          height: "23px",
          textAlign: "center",
          boxShadow: "none", // Remove the box shadow
          zIndex: activeMarker === null ? 1000 : 1000 + 1,
          fontSize: "10px",
          fontWeight: "400",
        }}
      >
        <p className="commute-time">
          <i className={`icon commute ${userLocation.method.name}`}></i>{" "}
          {job.commuteTime} {t("mins")}
        </p>
      </div>
    );
  };

  const homeIconSvg = `
  <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
  viewBox="0 0 512 512" style="enable-background:new 0 0 512 512;" xml:space="preserve">
<style type="text/css">
 .pin{fill:#${activeMarkerColor}}
 .home{fill:#ffffff}
</style>
<path class="pin" d="M251.6,0h8.9c99.9,3,177.8,84.6,176.8,184c-0.7,72.3-29.7,142.3-68.8,202c-25.2,38.4-54.3,73.9-87.4,106.6
 c-7.5,7.4-13.6,13.1-18.4,17.1c-1.2,1-3,1.8-5.3,2.3h-2.9c-2.5-0.6-4.3-1.4-5.3-2.4c-44.8-40.7-82.9-86.5-114.3-137.3
 c-36.2-58.7-62.6-129.2-60-198.8C78.6,78.2,156.6,2.9,251.6,0z"/>
<path class="home" d="M177.2,192.4c0.1,24.4,0.1,48.8,0,73.2c0,4.8,1.8,8.2,5.5,10.2c1.8,0.9,4.5,1.4,8.2,1.4c12.4,0,24.8,0,37.3,0
 c5.2,0,9.1-0.9,11.6-5.3c1-1.7,1.5-4.7,1.5-8.9c0-16.5,0-32.9,0-49.4c0-0.2,0.2-0.4,0.4-0.4c0,0,0,0,0,0h20.5c0.2,0,0.4,0.2,0.4,0.4
 c0,17.3,0,34.7,0,52c0,4.8,1.8,8.2,5.4,10.1c1.7,0.9,4.5,1.4,8.1,1.4c12.5,0,24.9,0,37.4,0c5.4,0,9.5-1,11.8-5.8
 c0.8-1.7,1.2-4.8,1.2-9.3c0-23.2,0-46.5,0-69.7c0-0.4,0.2-0.5,0.5-0.5c4.6,0,11,0.6,14.8-1.1c7-3.2,7.8-12.4,2.5-17.7
 c-27.5-27.5-55-55-82.5-82.4c-4-3.9-7.5-6.7-12.8-5.1c-1.9,0.6-4.5,2.5-7.7,5.7c-26.7,26.7-53.3,53.3-80,80c-4.3,4.3-7,8.1-4.6,13.9
 c3.5,8.3,12.7,6.4,19.9,6.6C177,191.9,177.2,192,177.2,192.4z"/>
</svg>
`;
  const getInitials = (firstName) => {
    if (firstName === undefined) return;
    const initials = firstName.charAt(0);
    return initials.toUpperCase();
  };
  return isLoaded ? (
    <>
      <div className="map-container">
        {isMobile ? (
          <Draggable
            ref={draggableRef}
            handle=".draggable-handle"
            onDrag={handleDrag}
            axis="y"
            position={initialPosition}
            // onStop={handleDragStop}
            onStop={(e, data) => setInitialPosition({ x: 0, y: data.y })}
            bounds={{
              top: 0,
              bottom: maxHeight,
            }}
          >
            <div className={`mapped-jobs ${showList ? "active" : ""}`}>
              <h5 className={"draggable-handle display-count "}>
                {t("displaying")}
                <span>
                  {" "}
                  {filteredJobCount === totalCount
                    ? ""
                    : filteredJobCount + "/"}
                  {totalCount}
                </span>{" "}
                {t("results")}
              </h5>
              <div
                className="job-results"
                style={{ height: `${dragAmount}px` }}
              >
                <ul className="job-listing">
                  {filterJobListings().map((job) => {
                    return (
                      <li
                        onClick={() => {
                          handleJobDataItemClick(job.id);
                        }}
                        className={`job-data  ${selectedJobIndex === job.id ? "active" : ""
                          }`}
                        key={job.id}
                        ref={(el) => (jobDataItemsRef.current[job.id] = el)}
                      >
                        <div className="xs-heading">{job.title}</div>
                        <span className="location">
                          {job.location?.address}
                        </span>
                        <ul className="job-data-highlights">
                          <li>
                            <i className="icon money"></i>
                            <span>{job.salaryText}</span>
                          </li>

                          <li>
                            <i className="icon type"></i>
                            <span>
                              {job.jobTypes?.map((x) => x).join(", ")}
                            </span>
                          </li>
                        </ul>
                        <div className="more-details">
                          <div className="consultant">
                            <div className="media">
                              {job.consultant && job.consultant.image ? (
                                <img
                                  src={job.consultant.image}
                                  alt={job.consultant?.firstname}
                                />
                              ) : (
                                <div className="initials">
                                  {getInitials(
                                    job.consultant?.firstname,
                                    job.consultant?.lastname
                                  )}
                                </div>
                              )}
                            </div>
                            <p className="posted">
                              {t("posted_by")} {job.consultant.firstname}{" "}
                              {job.consultant.lastname}
                              <span>{job.consultant.position}</span>
                            </p>
                          </div>
                          {center && (
                            <div className="commute">
                              <i
                                className={`icon commute ${userLocation.method.name}`}
                              ></i>
                              <span>
                                {job.commuteTime} {t("mins_by")}{" "}
                                {userLocation.method.name} {t("from")}{" "}
                                {userLocation.startingLocation.name}{" "}
                              </span>
                            </div>
                          )}

                          <div className="summary">
                            <div className="rte">
                              <p
                                dangerouslySetInnerHTML={{
                                  __html:
                                    job.searchSnippet === ""
                                      ? job.description
                                      : job.searchSnippet,
                                }}
                              ></p>
                            </div>
                            <div className="action">



                              {job.url !== "" ? (
                                <a href={job.url} className="btn" >
                                  <span>{t("see_full_details")}</span>
                                </a>
                              ) : (
                                <Link to={`?job=${job.slug}`} rel="noopener noreferrer" className="btn" onClick={() => {
                                  document.body.classList.remove("map-open");
                                  setShowMap(false);
                                }}>
                                  <span>{t("see_full_details")}</span>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {nextPageToken && totalCount !== loadCount && (
                  <button
                    type="button"
                    className="btn load-more"
                    onClick={handleNextPage}
                    style={{
                      display: isNextButtonVisible ? "block" : "none",
                    }}
                  >
                    <span>{t("load_more")}</span>
                  </button>
                )}
              </div>
            </div>
          </Draggable>
        ) : (
          <div className={`mapped-jobs ${showList ? "active" : ""}`}>
            <h5 className={"draggable-handle display-count "}>
              {filteredJobCount === jobs.length
                ? t("displaying")
                : t("displaying")}
              <span>
                {" "}
                {filteredJobCount === totalCount
                  ? ""
                  : filteredJobCount + "/"}
                {totalCount}
              </span>{" "}
              {t("results")}
            </h5>
            <div className="job-results">
              <ul className="job-listing">
                {filterJobListings().map((job, index) => {
                  return (
                    <li
                      onClick={() => {
                        handleJobDataItemClick(job.id);
                      }}
                      className={`job-data  ${selectedJobIndex === job.id ? "active" : ""
                        }`}
                      key={index}
                      ref={(el) => (jobDataItemsRef.current[job.id] = el)}
                    >
                      <div className="xs-heading">{job.title}</div>
                      <span className="location">
                        {job.location?.address}
                      </span>
                      <ul className="job-data-highlights">
                        <li>
                          <i className="icon money"></i>
                          <span>{job.salaryText}</span>
                        </li>

                        <li>
                          <i className="icon type"></i>
                          <span>
                            {job.jobTypes?.map((x) => x).join(", ")}
                          </span>
                        </li>
                      </ul>
                      <div className="more-details">
                        <div className="consultant">
                          <div className="media">
                            {job.consultant && job.consultant.image ? (
                              <img
                                src={job.consultant?.image}
                                alt={job.consultant?.firstname}
                              />
                            ) : (
                              <div className="initials">
                                {getInitials(
                                  job.consultant?.firstname,
                                  job.consultant?.lastname
                                )}
                              </div>
                            )}
                          </div>
                          <p className="posted">
                            {t("posted_by")} {job.consultant?.firstname}{" "}
                            {job.consultant?.lastname}
                            <span>{job.consultant?.position}</span>
                          </p>
                        </div>
                        {center && (
                          <div className="commute">
                            <i
                              className={
                                "icon commute " +
                                `${userLocation.method.name}`
                              }
                            ></i>
                            <span>
                              {job.commuteTime} mins by{" "}
                              {userLocation.method.name} from{" "}
                              {userLocation.startingLocation.name}{" "}
                            </span>
                          </div>
                        )}

                        <div className="summary">
                          <div className="rte">
                            <p
                              dangerouslySetInnerHTML={{
                                __html:
                                  job.searchSnippet === ""
                                    ? job.description
                                    : job.searchSnippet,
                              }}
                            ></p>
                          </div>
                          <div className="action">
                            {job.url !== "" ? (
                              <a href={job.url} className="btn" >
                                <span>{t("see_full_details")}</span>
                              </a>
                            ) : (
                              <Link to={`?job=${job.slug}`} className="btn" rel="noopener noreferrer" onClick={() => {
                                document.body.classList.remove("map-open");
                                setShowMap(false);
                              }}>
                                <span>{t("see_full_details")}</span>
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {nextPageToken && totalCount !== loadCount && (
                <button
                  type="button"
                  className="btn load-more"
                  onClick={handleNextPage}
                  style={{ display: isNextButtonVisible ? "block" : "none" }}
                >
                  <span>{t("load_more")}</span>
                </button>
              )}
            </div>
          </div>
        )}

        <div className="map">
          <div className="btn" onClick={toggleMap} id="returnListView">
            <span>
              <FontAwesomeIcon icon={faRectangleList} /> {t("list_view")}
            </span>
          </div>

          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={10}
            onError={(err) => console.error("Map loading error", err)}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onZoomChanged={() => {
              if (map) {
                setMapZoom(map.getZoom());
              }
            }}
            onBoundsChanged={() => {
              if (map) {
                setMapBounds(map.getBounds());
                updateFilteredJobCount();
              }
            }}
            options={{
              styles: customMapStyle,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              maxZoom: 16,
              scrollwheel: true,
              gestureHandling: "greedy",
            }}
          >
            {center && (
              <>
                <Marker
                  position={{ lat: center.lat, lng: center.lng }}
                  animation={window.google.maps.Animation.DROP}
                  icon={{
                    url: `data:image/svg+xml;utf8,${encodeURIComponent(
                      homeIconSvg
                    )}`,
                    anchor: new window.google.maps.Point(20, 40),
                    scaledSize: new window.google.maps.Size(40, 40),
                    fillColor: `#${markerColor}`,
                    fillOpacity: 1,
                    strokeWeight: 0,
                    //scale: 0.05, // Adjust the scale as needed
                  }}
                  style={{
                    color: `#${markerColor}`,
                    fontSize: "20px", // Adjust the size as needed
                    cursor: "pointer",
                  }}
                />
              </>
            )}

            <MarkerClusterer
              gridSize={5}
              maxZoom={16}
              minimumClusterSize={2}
              onClick={handleClusterClick}
              options={{
                styles: [
                  {
                    url: `data:image/svg+xml;utf8,<svg width="50" height="50" viewBox="0 0 100 100" version="1.1" xmlns="http://www.w3.org/2000/svg"><circle cx="49.74" cy="49.74" r="49.74" fill="%23${clickedCluster > 0 ? activeMarkerColor : markerColor
                      }"/></svg>`,
                    height: 30,
                    width: 30,
                    anchor: [0, 0],
                    textSize: 12,
                    textColor: "white",
                    zIndex: 2,
                  },
                ],
              }}
            >
              {(clusterer) => (
                <div>
                  {jobs.map((job, index) => (
                    <React.Fragment key={index}>
                      <Marker
                        key={index}
                        icon={{
                          path: `${faLocationPin.icon[4]}`,

                          anchor: new window.google.maps.Point(185, 500),
                          scaledSize: new window.google.maps.Size(20, 20),
                          fillColor:
                            activeMarker === null
                              ? `#${markerColor}`
                              : activeMarker === job.id
                                ? `#${activeMarkerColor}`
                                : `#${markerColor}`,

                          strokeWeight: 0,
                          fillOpacity: 1,
                          scale:
                            activeMarker === null
                              ? 0.05
                              : activeMarker === job.id
                                ? 0.055
                                : 0.05,
                        }}
                        position={{
                          lat: job.location?.lat,
                          lng: job.location?.lng,
                        }}
                        onClick={() => {
                          handleMarkerClick(job.id);
                        }}
                        clusterer={clusterer}
                        animation={window.google.maps.Animation.DROP}
                        zIndex={activeMarker === job.id ? 1000 : 0}
                      />
                      {center && job.commuteTime !== 0 && (
                        <OverlayView
                          position={{
                            lat: job.location.lat,
                            lng: job.location.lng,
                          }}
                          mapPaneName={OverlayView.OVERLAY_LAYER}
                          getPixelPositionOffset={(width, height) => ({
                            x: -(width / 2),
                            y: -height - 28, // Adjust the offset to position above the marker
                          })}
                          zIndex={1000 + 1}
                        >
                          <CustomInfoWindow
                            position={position}
                            activeMarker={activeMarker}
                            job={job}
                          />
                        </OverlayView>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </MarkerClusterer>
          </GoogleMap>
        </div>
      </div>
    </>
  ) : (
    <></>
  );
};

export default React.memo(Map);
